import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import Topbar from "@/components/Topbar";
import MetricCard from "@/components/MetricCard";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Receipt, Users, CurrencyInr, Warning, ChatCircleDots, Phone, Lightning, Spinner, ChatText } from "@phosphor-icons/react";
import { dashboardApi, remindersApi, seedApi, repliesApi } from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [replyStats, setReplyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([dashboardApi.stats(), repliesApi.stats()])
      .then(([s, r]) => { setStats(s); setReplyStats(r); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSeed = async () => {
    try {
      const r = await seedApi.demo();
      if (r.seeded) toast.success(`Seeded ${r.customers} customers & ${r.invoices} invoices`);
      else toast.info("Demo data already present");
      load();
    } catch { toast.error("Failed to seed demo data"); }
  };

  const runAgent = async () => {
    setRunning(true);
    try {
      await remindersApi.autoRun();
      toast.success("AI Agent is scanning overdue invoices...");
      setTimeout(load, 3500);
    } catch {
      toast.error("Failed to run agent");
    } finally { setTimeout(() => setRunning(false), 3000); }
  };

  if (loading && !stats) {
    return (
      <AppLayout>
        <Topbar title="Dashboard" subtitle="Loading your collections overview..." />
        <div className="p-10"><Spinner className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      </AppLayout>
    );
  }

  const empty = stats && stats.customers_count === 0;

  return (
    <AppLayout>
      <Topbar
        title="Dashboard"
        subtitle="Real-time payment recovery intelligence"
        actions={
          <Button
            data-testid="run-ai-agent-button"
            onClick={runAgent}
            disabled={running}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {running ? <Spinner className="h-4 w-4 animate-spin mr-2" /> : <Lightning weight="fill" className="h-4 w-4 mr-2" />}
            Run AI Agent
          </Button>
        }
      />
      <div className="px-6 lg:px-10 py-8 space-y-8">
        {empty && (
          <Card className="p-8 border border-border bg-muted/40">
            <h3 className="font-display text-2xl font-semibold">Welcome to Revmerito AI Collections</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              Add customers and overdue invoices manually, or load demo data to see the AI agent in action.
            </p>
            <Button data-testid="seed-demo-button" onClick={handleSeed} className="mt-5 bg-primary text-primary-foreground">
              Load Demo Data
            </Button>
          </Card>
        )}

        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          <MetricCard
            testId="metric-total-overdue"
            label="Total Overdue"
            value={formatCurrency(stats?.total_overdue || 0)}
            subValue={`${stats?.overdue_count || 0} invoices past due`}
            accent="danger"
            icon={Warning}
          />
          <MetricCard
            testId="metric-outstanding"
            label="Outstanding"
            value={formatCurrency(stats?.total_outstanding || 0)}
            subValue={`${(stats?.overdue_count || 0) + (stats?.pending_count || 0)} active invoices`}
            icon={CurrencyInr}
          />
          <MetricCard
            testId="metric-collected"
            label="Collected"
            value={formatCurrency(stats?.total_collected || 0)}
            subValue={`${stats?.paid_count || 0} invoices paid`}
            accent="success"
            icon={Receipt}
          />
          <MetricCard
            testId="metric-recovery-rate"
            label="Recovery Rate"
            value={`${stats?.recovery_rate || 0}%`}
            subValue={`${stats?.customers_count || 0} customers tracked`}
            icon={Users}
          />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          <div className="lg:col-span-2 border border-border bg-card rounded-md p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-display text-xl font-semibold">Communication Activity</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Last 7 days — WhatsApp & Voice</p>
              </div>
              <div className="flex items-center gap-4 text-xs font-mono">
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: "hsl(var(--chart-2))" }} /> WhatsApp</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: "hsl(var(--chart-3))" }} /> Voice</span>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats?.activity_last_7_days || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))", fontFamily: "IBM Plex Mono" }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))", fontFamily: "IBM Plex Mono" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                  <Line type="monotone" dataKey="whatsapp" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="voice" stroke="hsl(var(--chart-3))" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="border border-border bg-card rounded-md p-6">
            <h3 className="font-display text-xl font-semibold">Aging Buckets</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Overdue by days</p>
            <div className="h-64 mt-5">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.aging_buckets || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))", fontFamily: "IBM Plex Mono" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))", fontFamily: "IBM Plex Mono" }} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} formatter={(v) => formatCurrency(v)} />
                  <Bar dataKey="amount" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
          <div className="border border-border bg-card rounded-md p-6">
            <div className="flex items-center gap-2.5">
              <ChatCircleDots className="h-5 w-5 text-success" weight="duotone" />
              <h4 className="font-display text-lg font-semibold">WhatsApp Messages</h4>
            </div>
            <div className="font-mono tabular-nums text-4xl font-semibold mt-4">{stats?.whatsapp_sent || 0}</div>
            <p className="text-xs text-muted-foreground mt-2">Total messages dispatched by the AI agent</p>
          </div>
          <div className="border border-border bg-card rounded-md p-6">
            <div className="flex items-center gap-2.5">
              <Phone className="h-5 w-5 text-primary" weight="duotone" />
              <h4 className="font-display text-lg font-semibold">Voice Calls</h4>
            </div>
            <div className="font-mono tabular-nums text-4xl font-semibold mt-4">{stats?.voice_sent || 0}</div>
            <p className="text-xs text-muted-foreground mt-2">AI-initiated outbound reminder calls</p>
          </div>
          <div className="border border-border bg-card rounded-md p-6 ai-glow">
            <div className="flex items-center gap-2.5">
              <Lightning className="h-5 w-5 text-primary" weight="fill" />
              <h4 className="font-display text-lg font-semibold">AI Status</h4>
            </div>
            <div className="font-mono text-sm mt-4 text-foreground">GPT 6 Sol · Active</div>
            <p className="text-xs text-muted-foreground mt-2">Autonomous script generation & tone adaptation</p>
          </div>
        </section>

        {replyStats && (
          <section className="border border-border bg-card rounded-md p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <ChatText className="h-5 w-5 text-primary" weight="duotone" />
              <div>
                <h3 className="font-display text-xl font-semibold">AI Reply Classification</h3>
                <p className="text-xs text-muted-foreground mt-0.5">How customers respond — classified by GPT 6 Sol</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { key: "paid", label: "Paid", cls: "text-success", bg: "bg-success/10" },
                { key: "promise", label: "Promise", cls: "text-amber-600 dark:text-amber-400", bg: "bg-amber-500/10" },
                { key: "dispute", label: "Dispute", cls: "text-destructive", bg: "bg-destructive/10" },
                { key: "query", label: "Query", cls: "text-foreground", bg: "bg-muted" },
                { key: "other", label: "Other", cls: "text-muted-foreground", bg: "bg-muted" },
              ].map((c) => (
                <div
                  key={c.key}
                  data-testid={`reply-stat-${c.key}`}
                  className={`p-4 rounded-md border border-border ${c.bg}`}
                >
                  <div className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">{c.label}</div>
                  <div className={`font-mono tabular-nums text-3xl font-semibold mt-1.5 ${c.cls}`}>
                    {replyStats[c.key] || 0}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </AppLayout>
  );
}
